// src/App.js
import React, { useEffect, useRef, useState  } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { fetchAccounts } from "./services/FirestoreService";
import { useAuth} from "./contexts/GoogleAuthContext";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import Dashboard from "./pages/dashboard";
import CalculateForm from "./components/CalculateForm";
import ForexCalculateForm from "./components/ForexCalculateForm";
import Journal from "./pages/Journal";
import EconomicEvents from "./pages/EconomicEvents";
import Settings from "./pages/Settings";
import Stats from "./pages/Stats";
import Lessons from "./pages/Lessons";
import CreateAccountModal from "./components/CreateAccountModal";
import LoginScreen from "./components/LoginScreen";
import CalculatePropForm from "./components/CalculatePropForm";
import FullScreenSpinner from "./components/FullScreenSpinner";
import ForexCalculateProp from "./components/ForexCalculateProp";
import useIdleLogout from "./hooks/useIdleLogout";
import Subscribers from "./pages/Subscribers";
import { getAuth, signOut } from "firebase/auth";
import { checkActiveSubscriptionByEmail } from "./services/FirestoreService";
import HelpPage from "./pages/Helppage";
import Account from "./pages/Account";
import Mindset from "./pages/Mindset";
import EmotionalJournal from "./pages/EmotionalJournal";
import ProtectedRoute from "./routes/ProtectedRoute";
import CalculatePropFunded from "./components/CalculatePropFunded";
import Footer from './components/Footer';
import ForexCalculatePropFunded from "./components/ForexCalculatePropFunded";
import FuturesBacktest from "./components/FuturesBacktest";
import ForexBacktest from "./components/ForexBacktest";
import CalculateFormStocks from "./components/CalculateFormStocks";
import StocksBacktest from "./components/StocksBacktest";
import CalculateFormOptions from "./components/OptionsCalculateForm";
import OptionsBacktest from "./components/OptionsBacktest";
import MasterCalculate from "./components/MasterCalculate";
import MasterBacktest from "./components/MasterBacktest";
import WelcomeVideoModal from "./components/WelcomeVideoModal";
import { getUserWelcomeStatus, markWelcomeVideoAsSeen } from "./services/FirestoreService";
import ResetPassword from './components/ResetPassword';
import { doc, onSnapshot } from "firebase/firestore";
import UniversityPage from "./pages/UniversityPage";
import { db } from "./firebase";
const SUB_GATE_ENABLED = false;   

// Simple guard for private routes
 // function AdminGate({ allowed, children }) {
 //   return allowed ? children : <Navigate to="/" replace />;
 // }

function AdminGate({ children, fallback = null, allowed }) {
  // allowed should be computed *after* auth/claims are ready
  if (allowed === undefined) return fallback ?? <div>Checking access…</div>;
  return allowed ? children : <div className="p-6">Access denied.</div>;
}

function SubGate({ children, allowed, fallback = "/university" }) {
  if (allowed === undefined || allowed === null) return <FullScreenSpinner />;
  return allowed ? children : <Navigate to={fallback} replace />;
}


export default function App() {
  // ---- Auth (from your context) ----
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthPage = location.pathname.includes('/login') || location.pathname.includes('/reset-password');
  const [subAllowed, setSubAllowed] = useState(null);
  const [hasTrapoundU, setHasTrapoundU] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [checkingWelcome, setCheckingWelcome] = useState(true);

useEffect(() => {
    // 1. Wait until user is fully authenticated and sub is allowed
    if (!user?.uid || subAllowed !== true) {
      setCheckingWelcome(false); // Make sure it doesn't hang if they aren't logged in
      return;
    }

    let isMounted = true;

    // 2. Fetch the status once to avoid Firestore cache flashes
    const checkWelcomeStatus = async () => {
      try {
        const hasSeen = await getUserWelcomeStatus(user.uid);
        if (isMounted) {
          // Only show the video if we definitively know they haven't seen it
          setShowWelcome(!hasSeen);
        }
      } catch (error) {
        console.warn("Error fetching welcome video status:", error);
      } finally {
        if (isMounted) {
          setCheckingWelcome(false); // Mark the check as complete
        }
      }
    };

    checkWelcomeStatus();

    // 3. Clean up
    return () => { isMounted = false; };
  }, [user?.uid, subAllowed]);


   function withTimeout(promise, ms = 8000) {
     return Promise.race([
       promise,
       new Promise((_, reject) => setTimeout(() => reject(new Error("guard-timeout")), ms))
     ]);
   }

   // 2 hours = 7,200,000 milliseconds
     useIdleLogout(7200000);

  // Small grace window for silent session restore
  const [authProbeDone, setAuthProbeDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAuthProbeDone(true), 1000);
    return () => clearTimeout(t);
  }, []);

  // Safely push authenticated users to the dashboard if they are on root or login
  useEffect(() => {
    if (user && subAllowed === true) {
      // We switched to Popups, so we can safely auto-redirect away from /login again!
      if (location.pathname === "/" || location.pathname === "/login") { 
        navigate("/dashboard", { replace: true });
      }
    }
  }, [user, subAllowed, location.pathname, navigate]);



// Subscription gate
    useEffect(() => {
      

      // Give Firebase a second to grab the session before assuming they are logged out
      if (!authProbeDone && !user) {
        console.log("");
        return;
      }

      if (!user?.email) {
        setSubAllowed(false);
        return;
      }
      
      let mounted = true;

      (async () => {
        try {
          
          const res = await withTimeout(checkActiveSubscriptionByEmail(user.email));
          if (!mounted) return;

          
          
          // Inside your checkActiveSubscriptionByEmail resolution:
          const isAdmin = !!res?.admin;
          const allowed = !!(res?.allowed || isAdmin);
          const trapoundU = !!res?.hasTrapoundU;

          setIsAdmin(isAdmin);
          setSubAllowed(allowed);
          setHasTrapoundU(trapoundU); // Don't forget to declare this state at the top of App.js!
          setIsDemo(!!res?.isDemo);

          // Only boot them out if they don't have a sub AND they don't have Trapound U
          if (!allowed && !trapoundU) {
            navigate("/login", {
              replace: true,
              state: {
                denied: true,
                reason: res?.reason || "not active",
                email: user.email,
                customerId: res?.customerId || ""
              }
            });
          }
        } catch (e) {
          if (mounted) {
            setSubAllowed(false);
            navigate("/login", {
              replace: true,
              state: { denied: true, reason: "guard-error", email: user.email }
            });
          }
        }
      })();

      return () => { mounted = false; };
    }, [user, user?.email, navigate, authProbeDone]);





  // ---- Accounts (selection + data) ----
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAccountModal, setShowAccountModal] = useState(false);
  //const adminHiddenPaths = ["/__subs", "/__journal"];
  //if (!isAdmin && adminHiddenPaths.some(p => location.pathname.startsWith(p))) {
  //  navigate("/"); // block non-admins only
 // }

  useEffect(() => {
    if (!user) {
      setAccounts([]);
      setSelectedAccountId(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchAccounts(user.uid)
      .then((accs) => {
        setAccounts(accs);
        const last = localStorage.getItem("selectedAccountId");
        if (last && accs.some((a) => a.id === last)) {
          setSelectedAccountId(last);
        } else if (accs.length) {
          setSelectedAccountId(accs[0].id);
        } else {
          setSelectedAccountId(null);
        }
      })
      .catch(() => {
        setAccounts([]);
        setSelectedAccountId(null);
      })
      .finally(() => setLoading(false));
  }, [user]);


  // Ensure “Create Account” modal is closed when the user logs out
  useEffect(() => {
    if (!isLoggedIn && showAccountModal) setShowAccountModal(false);
  }, [isLoggedIn, showAccountModal]);

  useEffect(() => {
    if (selectedAccountId) {
      localStorage.setItem("selectedAccountId", selectedAccountId);
    }
  }, [selectedAccountId]);

  function handleAddAccount() {
    setShowAccountModal(true);
  }

  // ---- Auth gates ----
  // While we’re verifying subscription, don’t render the app or modals
  if (subAllowed === null && !isAuthPage) {
    return <FullScreenSpinner label="Checking your subscription..." />;
  }


  if (loading) return <FullScreenSpinner label="Loading your accounts..." />;


// No accounts yet → launch create flow (subscribers only)
if (isLoggedIn && subAllowed === true && accounts.length === 0 && location.pathname !== "/__subs") {
    // Wait until we know if they should see the welcome video
    if (checkingWelcome) {
      return <FullScreenSpinner label="Preparing your dashboard..." />;
    }

    return (
      <>
        <CreateAccountModal
          userId={user?.uid}
          accountCount={accounts.length}
          isDemo={isDemo}
          isAdmin={isAdmin}
          onAccountCreated={(id) => {
                 if (!user?.uid) return; 
                 fetchAccounts(user?.uid).then((accs) => {
              setAccounts(accs);
              setSelectedAccountId(id);
            });
          }}
          onClose={() => {}}
        />
        
        {/* Render the welcome video OVER the create account modal if needed */}
        {showWelcome && (
          <WelcomeVideoModal 
            onClose={async () => {
              await markWelcomeVideoAsSeen(user.uid);
              setShowWelcome(false);
            }} 
          />
        )}
      </>
    );
}

  // Helper to pick the right calculator by account type + prop flag
  function renderCalculatorRoute() {
    if (!selectedAccountId) {
      return <div className="p-8 text-center text-gray-400">Select or create an account.</div>;
    }

    const acct = accounts.find((a) => a.id === selectedAccountId);
    if (!acct) {
      return <div className="p-8 text-center text-gray-400">Account not found.</div>;
    }

    // Detect evaluation flag (backward-compatible)
    const isPropEval =
      acct?.settings?.isPropFirm === true ||
      acct?.settings?.isProp === true ||
      (typeof acct?.settings?.isPropFirm === "string" && acct.settings.isPropFirm.toLowerCase() === "true") ||
      (typeof acct?.settings?.isProp === "string" && acct.settings.isProp.toLowerCase() === "true");

    // Detect funded flag/tag
    const tagVal =
      (acct?.tag && String(acct.tag)) ||
      (acct?.settings?.tag && String(acct.settings.tag)) ||
      "";
    const isPropFunded =
      tagVal.toLowerCase() === "prop funded" ||
      acct?.settings?.isPropFunded === true ||
      (typeof acct?.settings?.isPropFunded === "string" &&
        acct.settings.isPropFunded.toLowerCase() === "true");

    // Detect backtest flag (robust)
    const isBacktest =
      acct?.settings?.isBacktest === true ||
      (typeof acct?.settings?.isBacktest === "string" && acct.settings.isBacktest.toLowerCase() === "true") ||
      acct?.isBacktest === true ||
      (typeof acct?.isBacktest === "string" && acct.isBacktest.toLowerCase() === "true");

    // --- FUTURES ---
    if (acct.type === "Futures") {
      if (isPropFunded) {
        return <CalculatePropFunded userId={user?.uid} accountId={selectedAccountId} />;
      }
      if (isPropEval) {
        return <CalculatePropForm userId={user?.uid} accountId={selectedAccountId} />;
      }
      if (isBacktest) {
        return <FuturesBacktest userId={user?.uid} accountId={selectedAccountId} />;
      }
      return <CalculateForm userId={user?.uid} accountId={selectedAccountId} />;
    
    // --- FOREX ---
    } else if (acct.type === "Forex") {
      if (isPropFunded) {
        return <ForexCalculatePropFunded userId={user?.uid} accountId={selectedAccountId} />;
      }
      if (isPropEval) {
        return <ForexCalculateProp userId={user?.uid} accountId={selectedAccountId} />;
      }
      if (isBacktest) {
        return <ForexBacktest userId={user?.uid} accountId={selectedAccountId} />;
      }
      return <ForexCalculateForm userId={user?.uid} accountId={selectedAccountId} />;

// --- STOCKS (New Logic) ---
    } else if (acct.type === "Stocks" || acct.type === "Stock") {
      // Check for backtest flag in top-level or settings
      const isBacktest = 
        acct.isBacktest === true || 
        acct.settings?.isBacktest === true ||
        String(acct.tag || "").toLowerCase().includes("backtest");

      if (isBacktest) {
        return <StocksBacktest userId={user?.uid} accountId={selectedAccountId} />;
      }
      
      return <CalculateFormStocks userId={user?.uid} accountId={selectedAccountId} />;
// --- Mixed (New Logic) ---
    } else if (acct.type === "Mixed" || acct.type === "Mixed") {
      // Check for backtest flag in top-level or settings
      const isBacktest = 
        acct.isBacktest === true || 
        acct.settings?.isBacktest === true ||
        String(acct.tag || "").toLowerCase().includes("backtest");

      if (isBacktest) {
        return <MasterBacktest userId={user?.uid} accountId={selectedAccountId} />;
      }
      
      // ADD isAdmin={isAdmin} TO THE LINE BELOW:
      return <MasterCalculate userId={user?.uid} accountId={selectedAccountId} isAdmin={isAdmin} />;
  } else if (acct.type === "Options" || acct.type === "Option") {
      // Check for backtest flag in top-level or settings
      const isBacktest = 
        acct.isBacktest === true || 
        acct.settings?.isBacktest === true ||
        String(acct.tag || "").toLowerCase().includes("backtest");

      if (isBacktest) {
        return <OptionsBacktest userId={user?.uid} accountId={selectedAccountId} />;
      }
      
      return <CalculateFormOptions userId={user?.uid} accountId={selectedAccountId} />;
    }


    return <div className="p-8 text-center text-gray-400">Unknown account type.</div>;
  }


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Hide header on auth pages */}
      {!isAuthPage && (
        <Header accounts={accounts} selectedAccountId={selectedAccountId} />
      )}

      <div className="flex flex-1">
        {/* Hide sidebar on auth pages */}
        {!isAuthPage && (
      <Sidebar
          accounts={accounts}
          selectedAccountId={selectedAccountId}
          setSelectedAccountId={setSelectedAccountId}
          subAllowed={subAllowed}
          hasTrapoundU={hasTrapoundU}
        />
        )}

        {/* Adjust padding based on whether the sidebar is visible */}
        <main className={`flex-1 ${!isAuthPage ? "pb-20 md:pb-0 md:pl-20" : ""}`}>
          <Routes>
            {/* PUBLIC */}
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/login" element={<LoginScreen />} />

            {/* PROTECTED AREA */}
            <Route element={<ProtectedRoute />}>
              
              {/* Smart Root Redirect: Goes to dashboard if full sub, otherwise university */}
              <Route path="/" element={<Navigate to={subAllowed ? "/dashboard" : "/university"} replace />} />
              

              <Route
                path="/dashboard"
                element={
                  <SubGate allowed={subAllowed}>
                    {selectedAccountId ? (
                      <Dashboard
                        accounts={accounts}
                        selectedAccountId={selectedAccountId}
                        setSelectedAccountId={setSelectedAccountId}
                        onAddAccount={handleAddAccount}
                        isAdmin={isAdmin}
                      />
                    ) : (
                      <div className="p-8 text-center text-gray-400">
                        Select or create an account.
                      </div>
                    )}
                  </SubGate>
                }
              />

              <Route
                path="/__journal"
                element={
                  <AdminGate allowed={!!user && isAdmin}>
                    {selectedAccountId ? (
                      <Journal
                        userId={user?.uid}
                        accountId={selectedAccountId}
                        isAdmin={isAdmin}  // ← pass it
                      />
                    ) : (
                      <div className="p-8 text-center text-gray-400">Select or create an account.</div>
                    )}
                  </AdminGate>
                }
              />

              <Route
                path="/journal"
                element={
                  <SubGate allowed={subAllowed}>
                    {selectedAccountId ? (
                      <Journal
                        userId={user?.uid}
                        accountId={selectedAccountId}
                        isAdmin={isAdmin}   // ← pass it
                      />
                    ) : (
                      <div className="p-8 text-center text-gray-400">Select or create an account.</div>
                    )}
                  </SubGate>
                }
              />

              <Route path="/calculate" element={<SubGate allowed={subAllowed}>{renderCalculatorRoute()}</SubGate>} />
              <Route path="/forex-calculate" element={<SubGate allowed={subAllowed}>{renderCalculatorRoute()}</SubGate>} />
              <Route path="/forex-calculate-prop" element={<SubGate allowed={subAllowed}>{renderCalculatorRoute()}</SubGate>} />
              <Route path="/forex-calculate-prop-funded" element={<SubGate allowed={subAllowed}>{renderCalculatorRoute()}</SubGate>} />
              
              <Route path="/events" element={
                <SubGate allowed={subAllowed}>
                  <EconomicEvents />
                </SubGate>
              } />

              {/* UNGATED: Settings, Account, Help - Everyone needs these */}
              <Route
                path="/settings"
                element={
                  selectedAccountId ? (
                    <Settings userId={user?.uid} accountId={selectedAccountId} />
                  ) : (
                    <div className="p-8 text-center text-gray-400">
                      Select or create an account.
                    </div>
                  )
                }
              />

              <Route
                path="/stats"
                element={
                  <SubGate allowed={subAllowed}>
                    {selectedAccountId ? (
                     <Stats userId={user?.uid} accountId={selectedAccountId} />
                    ) : (
                      <div className="p-8 text-center text-gray-400">
                        Select or create an account.
                      </div>
                    )}
                  </SubGate>
                }
              />

              {/* UNGATED */}
              <Route path="/help" element={<HelpPage />} />
              <Route path="/account" element={<Account />} />
              
              <Route path="/lessons" element={
                <SubGate allowed={subAllowed}>
                  <Lessons userId={user?.uid} />
                </SubGate>
              } />

              <Route path="/mindset" element={
                <SubGate allowed={subAllowed}>
                  <Mindset />
                </SubGate>
              } />

              {/* NEW: Emotional Journal Route */}
              <Route
                path="/emotional-journal"
                element={
                  <SubGate allowed={subAllowed}>
                    {selectedAccountId ? (
                      <EmotionalJournal userId={user?.uid} accountId={selectedAccountId} />
                    ) : (
                      <div className="p-8 text-center text-gray-400">
                        Select or create an account.
                      </div>
                    )}
                  </SubGate>
                }
              />

              {/* NEW: University Route */}
              <Route 
                path="/university" 
                element={
                  <SubGate allowed={hasTrapoundU || subAllowed}>
                    <UniversityPage />
                  </SubGate>
                } 
              />

               {/* Admin-only page */}
               <Route
                 path="/__subs"
                 element={
                   <AdminGate allowed={!!user && isAdmin}>
                     <Subscribers userId={user?.uid} />
                   </AdminGate>
                 }
              />

              {/* Catch-all inside protected area → dashboard */}
              <Route path="*" element={<Navigate to={subAllowed ? "/dashboard" : "/university"} replace />} />
            </Route>
          </Routes>

          {/* Create Account Modal */}
           {isLoggedIn && showAccountModal && (
              <CreateAccountModal
               userId={user?.uid}
              accountCount={accounts.length}
              isDemo={isDemo}
              isAdmin={isAdmin}
              onAccountCreated={(id) => {
                 if (!user?.uid) return;
                 fetchAccounts(user?.uid).then((accs) => {
                  setAccounts(accs);
                  setSelectedAccountId(id);
                });
                setShowAccountModal(false);
              }}
              onClose={() => setShowAccountModal(false)}
            />
          )}
        </main>


              <Footer user={user} />
      </div>
      {/* Put it here so it renders over EVERYTHING */}
      {showWelcome && (
        <WelcomeVideoModal 
          onClose={async () => {
            await markWelcomeVideoAsSeen(user.uid);
            setShowWelcome(false);
          }} 
        />
      )}
    </div>
  );


}